<template>
  <article>
    <activity-stage :customRootContainerClass="'spacebar'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div class="steps-container">
          <figure class="step-image">
            <img alt="Main image" :src="imagePath" v-if="imagePath" />
          </figure>
          <ul class="text-steps">
            <li
              v-for="(step, stepIndex) in currentRound.steps"
              :key="`${progress.roundIndex}-${stepIndex}`"
              :class="`list-step step-${stepIndex}`"
            >
              <color-block-text
                v-if="step.isTyped"
                :class="getTextSize"
                :text="step.answer"
                :text-step="stepIndex"
              ></color-block-text>
              <p v-else :class="`static-text ${getTextSize}`">
                {{ step.answer }}
              </p>
            </li>
          </ul>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import ActivityStage from 'components/activity/ActivityStage.vue';
import ColorBlockText from 'components/text-displays/ColorBlockText.vue';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import TouchAndHoldHelper from 'mixins/TouchAndHoldHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import { type } from 'os';

export default {
  components: {
    ActivityStage,
    ColorBlockText
  },

  mixins: [TypingActivityHelper, ActivityManager, TouchAndHoldHelper, ErrorLogger],

  data() {
    return {
      activityName: 'Making Space',
      imagePath: ''
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    getTextSize() {
      return this.currentRound.steps.length > 15 ? 'small' : '';
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds(this.getActivityState());
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ steps }) =>
        steps.forEach(({ activityImage }) => this.queueImage(activityImage?.image?.src))
      );

      return this.downloadImages();
    },

    getKeyboardConfig() {
      const { characters, keyboardType, roofs } = this.activityData.keyboard;
      const stringChars = typeof characters === 'string';
      const touchAndHold = stringChars && characters.length < 26;
      // eslint-disable-next-line
      let charsWithSpace = characters + ' ';

      return {
        keys: touchAndHold || stringChars ? charsWithSpace : characters,
        touchAndHold,
        keyboardType,
        roofs
      };
    },

    getActivityState() {
      const {
        rounds,
        keyboard: { characters }
      } = this.activityData;
      const touchAndHold = typeof characters === 'string' && characters.length < 26;
      const instructionsType = `makingspace${touchAndHold ? '' : 'nth'}`;

      return {
        rounds,
        instructions: {
          written: `${touchAndHold ? 'Hold the target. ' : ''}Type the spaces and words.`,
          voice: `${this.voiceInstructionsPrefix}/${instructionsType}-ma-1.mp3`
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.hideAllSteps();
        this.fadeInStep(this.progress.stepIndex);
        this.shouldSkipText();
      });
      this.shouldChangeImage();
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();
      this.fadeInStep(this.progress.stepIndex);
      this.shouldChangeImage();
      this.shouldSkipText();
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(
        { ...attempt, character: attempt.character === '' ? ' ' : attempt.character },
        this.isEndOfRound,
        !this.isEndOfRound
      );

      if (this.isEndOfCurrentWord) {
        this.handleStepEnd();
      }
      if (this.isEndOfRound) {
        this.sndBell();
        this.handleRoundEnd();
      }
    },

    handleStepEnd() {
      this.finishStep();
      this.fadeInStep(this.progress.stepIndex);
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (this.isEndOfActivity) {
        timeline
          .delay(1)
          .to('.steps-container', 0.5, { opacity: 0 })
          .call(this.completeActivity);
      } else {
        this.resetImagePath();
        timeline
          .to('.steps-container', 1, { opacity: 0 })
          .call(this.setupNewRound)
          .call(this.shouldChangeImage)
          .call(this.$nextTick, [
            () =>
              timeline
                .set('.list-step', { opacity: 0 })
                .set(`.step-${this.progress.stepIndex}`, { opacity: 1 })
                .call(this.shouldSkipText)
                .to('.steps-container', 0.5, { opacity: 1 })
          ]);
      }
    },

    fadeInStep(stepIndex) {
      TweenMax.to(`.list-step.step-${stepIndex}`, 0.5, { opacity: 1 });
    },

    hideAllSteps() {
      TweenMax.set('.list-step', { opacity: 0 });
    },

    shouldSkipText() {
      if (!this.currentStep.isTyped) {
        this.completeStep();
        this.setCurrentStep();
        this.fadeInStep(this.progress.stepIndex);
      }
    },

    shouldChangeImage() {
      if (this.currentStep?.activityImage?.image?.src) {
        this.imagePath = this.currentStep?.activityImage?.image?.src;
      }
    },

    resetImagePath() {
      this.imagePath = '';
    }
  },
  watch: {
    currentRound(newVal) {
      if (newVal?.steps && newVal.steps.length) {
        const phrase = newVal.steps
          .filter(step => step.isTyped)
          .map(step => step.answer)
          .join('');
        this.setTotalKeyboardingSegment(phrase);
      }
    },
    currentStep(newVal) {
      if (newVal?.answer) {
        let instructions = `Type the ${
          newVal.answer === ' '
            ? 'space key.'
            : `character${newVal.answer.length > 1 ? 's' : ''} - ${newVal.answer}`
        }`;

        this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.spacebar {
  ul {
    list-style-type: none;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;

    @media (max-width: 690px) {
      justify-content: flex-start;
    }

    p {
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: hwtprint;
      font-size: 29px;

      &.static-text {
        letter-spacing: 3px;
        color: #000;
      }

      &.small {
        font-size: 26px;
      }
    }
  }

  .step-image {
    margin: 0px;
    min-height: 290px;
    text-align: center;

    img {
      height: 100%;
      max-height: 250px;
    }
  }
}

.chrome-os,
.tablet {
  .spacebar {
    .step-image {
      min-height: 200px;

      img {
        max-height: 160px;
      }
    }
  }
}
</style>
